import React, { useEffect, useRef, useState } from 'react';
import { ApplicationIcon, Icon, Typography } from '@saviynt/design-system';

import testIds from '../../../../constants/testIds';
import useLocalization from '../../../../hooks/use-localize';
import { type AttributeMapping } from '../../constants/attributeConstants';
import AttributeDropdown from '../AttributeDropdown/AttributeDropdown';

import TextFieldsSVG from './TextFieldsSVG';
import TrashIconSVG from './TrashIconSVG';

import './AttributeRow.css';

interface Props {
  row: AttributeMapping;
  mappings: AttributeMapping[];
  setMappings: React.Dispatch<React.SetStateAction<AttributeMapping[]>>;
  editingServiceNowId: string | null;
  editingSaviyntId: string | null;
  setEditingServiceNowId: (id: string | null) => void;
  setEditingSaviyntId: (id: string | null) => void;
  serviceNowOptions: string[];
  saviyntOptions: string[];
  /** Called when the delete icon is clicked */
  onDelete: () => void;
  /** globally disabled editing */
  isDisabled?: boolean;
}

/**
 * Attribute Row Component
 */
function AttributeRow({
  row,
  mappings,
  setMappings,
  editingServiceNowId,
  editingSaviyntId,
  setEditingServiceNowId,
  setEditingSaviyntId,
  serviceNowOptions,
  saviyntOptions,
  onDelete,
  isDisabled = false,
}: Props): JSX.Element {
  const formatMessage = useLocalization();
  const serviceNowDropdownRef = useRef<HTMLDivElement | null>(null);
  const saviyntDropdownRef = useRef<HTMLDivElement | null>(null);

  const [dropdownDirection, setDropdownDirection] = useState<'up' | 'down'>(
    'down'
  );
  const [searchText, setSearchText] = useState('');

  const handleUpdate = (
    id: string,
    newValue: string,
    field: 'serviceNowAttributeName' | 'saviyntAttributeName'
  ): void => {
    setMappings((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, [field]: newValue } : item
      )
    );

    if (field === 'serviceNowAttributeName') {
      setEditingServiceNowId(null);
    } else {
      setEditingSaviyntId(null);
    }

    setSearchText('');
  };

  useEffect(() => {
    const handleClickOutside = (evt: MouseEvent) => {
      if (
        serviceNowDropdownRef.current &&
        !serviceNowDropdownRef.current.contains(evt.target as Node)
      ) {
        setEditingServiceNowId(null);
      }

      if (
        saviyntDropdownRef.current &&
        !saviyntDropdownRef.current.contains(evt.target as Node)
      ) {
        setEditingSaviyntId(null);
      }
    };

    const handleEsc = (evt: KeyboardEvent) => {
      if (evt.key === 'Escape') {
        setEditingServiceNowId(null);
        setEditingSaviyntId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [setEditingServiceNowId, setEditingSaviyntId]);

  useEffect(() => {
    const ref =
      editingServiceNowId === row.id
        ? serviceNowDropdownRef
        : saviyntDropdownRef;

    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      setDropdownDirection(
        spaceBelow < 250 && spaceAbove > spaceBelow ? 'up' : 'down'
      );
    }
  }, [editingServiceNowId, editingSaviyntId, row.id]);

  return (
    <div
      className='attribute-mappings__row'
      data-testid={testIds.ATTRIBUTE_ROW}>
      {/* ServiceNow column */}
      <div className='attribute-mappings__column'>
        <div className='attribute-mappings__attribute'>
          <div className='attribute-mappings__attribute-icon'>
            <TextFieldsSVG />
          </div>
          <div className='attribute-name-cell'>
            <button
              type='button'
              className='attribute-name-display'
              disabled={isDisabled}
              onClick={() => {
                if (!isDisabled) {
                  setEditingServiceNowId(row.id);
                  setEditingSaviyntId(null);
                  setSearchText('');
                }
              }}>
              <Typography
                kind='body2'
                color={isDisabled ? '--color-foreground-neutral-disabled' : ''}>
                {row.serviceNowAttributeName ||
                  formatMessage('INTEGRATIONS.ATTRIBUTE_ROW.EMPTY_PLACEHOLDER')}
              </Typography>
            </button>

            {editingServiceNowId === row.id && (
              <AttributeDropdown
                ref={serviceNowDropdownRef}
                rowId={row.id}
                field='serviceNowAttributeName'
                mappings={mappings}
                options={serviceNowOptions}
                onSelect={handleUpdate}
                searchText={searchText}
                setSearchText={setSearchText}
                dropdownDirection={dropdownDirection}
              />
            )}
          </div>
        </div>
        <Typography kind='body2'>{row.serviceNowAttributeValue}</Typography>
      </div>

      {/* Arrow */}
      <div className='attribute-mappings__arrow'>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='80'
          height='72'
          viewBox='0 0 80 72'>
          <path
            d='M3.36 36c0 1.47 1.19 2.67 2.67 2.67 1.47 0 2.67-1.2 2.67-2.67 0-1.47-1.2-2.67-2.67-2.67-1.48 0-2.67 1.2-2.67 2.67zm71.04.35c.2-.2.2-.52 0-.71l-3.18-3.18a.5.5 0 0 0-.71 0c-.2.2-.2.52 0 .71l2.83 2.83-2.83 2.83c-.2.2-.2.52 0 .71.2.2.52.2.71 0l3.18-3.18zM6.03 36v.5h68.01v-1H6.03V36z'
            fill='#254B86'
          />
        </svg>
      </div>

      {/* Saviynt column */}
      <div className='attribute-mappings__column'>
        <div className='attribute-mappings__attribute'>
          <div className='attribute-mappings__attribute-icon'>
            <TextFieldsSVG />
          </div>
          <div className='attribute-name-cell'>
            <button
              type='button'
              className='attribute-name-display'
              disabled={isDisabled}
              onClick={() => {
                if (!isDisabled) {
                  setEditingSaviyntId(row.id);
                  setEditingServiceNowId(null);
                  setSearchText('');
                }
              }}>
              <Typography
                kind='body2'
                color={isDisabled ? '--color-foreground-neutral-disabled' : ''}>
                {row.saviyntAttributeName ||
                  formatMessage('INTEGRATIONS.ATTRIBUTE_ROW.EMPTY_PLACEHOLDER')}
              </Typography>
            </button>

            {editingSaviyntId === row.id && (
              <AttributeDropdown
                ref={saviyntDropdownRef}
                rowId={row.id}
                field='saviyntAttributeName'
                mappings={mappings}
                options={saviyntOptions}
                onSelect={handleUpdate}
                searchText={searchText}
                setSearchText={setSearchText}
                dropdownDirection={dropdownDirection}
              />
            )}
          </div>
        </div>
        <Typography kind='body2'>{row.saviyntAttributeValue}</Typography>
      </div>

      {/* Delete button */}
      {!isDisabled && (
        <button
          type='button'
          className='attribute-mappings__delete'
          onClick={onDelete}
          aria-label={formatMessage(
            'INTEGRATIONS.ATTRIBUTE_ROW.DELETE_ARIA_LABEL'
          )}
          data-testid={testIds.ATTRIBUTE_ROW_DELETE_BUTTON}>
          <TrashIconSVG />
        </button>
      )}
    </div>
  );
}

AttributeRow.defaultProps = {
  isDisabled: false,
};

/**
 * Header subcomponent to render column headers for the attribute grid.
 */
AttributeRow.Header = function Header() {
  const formatMessage = useLocalization();

  return (
    <>
      <div className='attribute-mappings__cell attribute-mappings__cell--header'>
        <ApplicationIcon logo='ServiceNow' size='xSmall' />
        <Typography kind='body1-bold'>
          {formatMessage(
            'INTEGRATIONS.ATTRIBUTE_ROW.HEADER.SERVICENOW_FIELD',
            'ServiceNow Field'
          )}
        </Typography>
      </div>
      <div className='attribute-mappings__arrow-header-spacer' />
      <div className='attribute-mappings__cell attribute-mappings__cell--header'>
        <Icon kind='SaviyntLogoFilledSeparateColors' size='Small' />
        <Typography kind='body1-bold'>
          {formatMessage(
            'INTEGRATIONS.ATTRIBUTE_ROW.HEADER.SAVIYNT_FIELD',
            'Saviynt Field'
          )}
        </Typography>
      </div>
    </>
  );
};

export default AttributeRow;
